import { beginAuthOwner, currentAuthOwner, waitForAuthCookies, writeAuthCookies } from '@/shared/utils/authCookieOwner';
import { makeAutoObservable, runInAction } from 'mobx';
import { clearCheckoutPaymentObservations, invalidateCheckoutObservation } from '@/shared/utils/checkoutPaymentObservation';
import { captureMessage, withScope } from '@sentry/nextjs';
import type {
  PartnerAuthService,
  PartnerLoginRequest,
  PartnerSessionValidateResponse,
} from '@/core/infrastructure/services/PartnerAuthService';
import type {
  OTPAuthService,
  EmailLookupRequest,
  OTPLoginRequest,
  OTPRegisterRequest,
  OTPRequestParams,
  OTPVerifyPurpose,
  PhoneLookupRequest,
} from '@/core/infrastructure/services/OTPAuthService';
import type { AnonymousSessionService } from '@/core/infrastructure/services/AnonymousSessionService';
import type { CampaignBootstrapService } from '@/core/infrastructure/services/CampaignBootstrapService';
import { OTPAuthenticationUseCase } from '@/core/application/use-cases/auth/OTPAuthenticationUseCase';
import { format } from 'date-fns';
import {
  buildCampaignBootstrapSession,
  clearCampaignBootstrapSession as clearStoredCampaignBootstrapSession,
  persistCampaignBootstrapSession,
  readCampaignBootstrapSession,
  type CampaignBootstrapResponse,
} from '@/shared/utils/campaignBootstrap';
import { resolveMarketingConsent } from '@/shared/utils/marketingConsent';
import { persistShopAuthHandoffPayload } from '@/shared/utils/shopAuthHandoff';
import { getApiErrorMessage } from '@/shared/utils/apiErrorMessage';

export type AuthRecoveryResult = 'authenticated' | 'rejected' | 'unavailable' | 'superseded';

export type RegistrationStatus =
  | 'REGISTERED'
  | 'PHONE_VERIFIED'
  | 'KYC_BASIC_COMPLETED'
  | 'TERMS_ACCEPTED'
  | 'FULLY_ONBOARDED';

export type RegistrationStatusDiagnosticReason =
  | 'missing'
  | 'invalid_type'
  | 'unsupported'
  | 'fetch_failed';

export interface RegistrationStatusDiagnostic {
  reason: RegistrationStatusDiagnosticReason;
  source: string;
  rawStatus?: string;
  detail?: string;
  timestamp: number;
}

const ALLOWED_REGISTRATION_STATUSES: ReadonlySet<RegistrationStatus> = new Set([
  'REGISTERED',
  'PHONE_VERIFIED',
  'KYC_BASIC_COMPLETED',
  'TERMS_ACCEPTED',
  'FULLY_ONBOARDED',
]);
const ALLOWED_REGISTRATION_GENDERS = new Set(['male', 'female']);
const ALLOWED_EMPLOYMENT_STATUSES = new Set([
  'EMPLOYED',
  'SELF_EMPLOYED',
  'UNEMPLOYED',
  'STUDENT',
  'RETIRED',
]);
const REGISTRATION_STATUS_DIAGNOSTIC_REPORT_WINDOW_MS = 5 * 60 * 1000;
const REGISTRATION_STATUS_DIAGNOSTIC_SENTRY_ENABLED =
  process.env.NEXT_PUBLIC_AUTH_STATUS_DIAGNOSTICS_SENTRY === 'true';
const OTP_SESSION_STORAGE_KEY = 'motito_otp_session';

type ServerSessionCookieResult =
  | { ok: true }
  | { ok: false; status?: number; errorMessage: string };

export class AuthStore {
  // State
  currentUser: User | null = null;
  isAuthenticated = false;
  isLoading = false;
  isHydrated = false;
  isSigningIn = false;
  isSigningOut = false;
  error: string | null = null;
  errors: AuthError[] = [];

  loginAuthSession: LoginAuthSession | null = null;
  // Phone authentication state
  phoneAuthSession: PhoneAuthSession | null = null;
  isPhoneAuthInProgress = false;
  isSendingOtp = false;
  isVerifyingOtp = false;

  // New authentication states
  partnerAuthSession: PartnerAuthSession | null = null;
  otpSession: OTPSession | null = null;
  campaignBootstrapSession: CampaignBootstrapSession | null = null;
  anonymousSessionActive = false;

  // Loading states for new auth methods
  isValidatingPartnerSession = false;
  isRegisteringPartnerUser = false;
  isLoggingInPartnerUser = false;
  isLookingUpPhone = false;
  isRequestingOTP = false;
  isRegisteringWithOTP = false;
  isLoggingInWithOTP = false;
  isRedeemingCampaignBootstrap = false;
  isRegisteringWithCampaignBootstrap = false;

  // Login state
  lastLoginMethod: 'email' | 'phone' | 'partner' | 'otp' | 'campaign' | null =
    null;
  rememberMe = false;

  // Session management
  sessionExpiresAt: Date | null = null;
  isSessionExpiring = false;

  // Registration data management
  registrationData: RegistrationData = {};
  isUpdatingRegistration = false;
  registrationStatus: RegistrationStatus | null = null;
  registrationStatusDiagnostic: RegistrationStatusDiagnostic | null = null;
  private lastRegistrationDiagnosticSentryKey: string | null = null;
  private lastRegistrationDiagnosticSentryAt = 0;
  signUpStepNumber = 0;

  constructor(
    private readonly authenticateUserUseCase: OTPAuthenticationUseCase,
    private readonly partnerAuthService: PartnerAuthService,
    private readonly otpAuthService: OTPAuthService,
    private readonly anonymousSessionService: AnonymousSessionService,
    private readonly campaignBootstrapService: CampaignBootstrapService
  ) {
    makeAutoObservable<AuthStore, 'authGeneration' | 'initialization'>(this, {
      authGeneration: false,
      initialization: false,
    });
    // Avoid running client-side hydration logic during SSR. Otherwise the server can
    // render a different initial UI than the browser (causing hydration errors).
    if (typeof window !== 'undefined') {
      this.loadRegistrationData();
      this.loadOTPSession();
      const authGeneration = this.authGeneration;
      window.setTimeout(() => {
        void this.initializeAuth(authGeneration);
      }, 0);
    }
  }

  private get authGeneration(): number { return currentAuthOwner(); }
  private initialization: { owner: number; promise: Promise<AuthRecoveryResult> } | null = null;

  private beginAuthOperation(): number {
    this.isLoading = false;
    return beginAuthOwner();
  }

  private assertAuthOperation(generation: number): void {
    if (generation !== this.authGeneration) {
      throw new Error('Your sign-in session changed. Please try again.');
    }
  }

  private settleAuthInitialization(generation: number): void {
    if (generation !== this.authGeneration) return;
    this.isHydrated = true;
    this.isLoading = false;
  }

  private prepareAuthReplacement(generation: number): void {
    this.assertAuthOperation(generation);
    // A dispatched producer can set cookies even if its response is lost. Keep
    // cached profile data, but do not present it as the next cookie's identity.
    this.isAuthenticated = false;
    this.sessionExpiresAt = null;
    this.lastLoginMethod = null;
    this.clearPersistedAuthSession();
    this.clearPersistedAuthTokens();
    if (typeof window !== 'undefined') {
      localStorage.removeItem('motito_auth_token');
      localStorage.removeItem('motito_auth_expiry');
    }
  }

  beginSessionReplacement(): number {
    const generation = this.beginAuthOperation();
    this.prepareAuthReplacement(generation);
    return generation;
  }

  private async finishAuthOperation(generation: number, replacement = false): Promise<void> {
    if (generation !== this.authGeneration || (this.isHydrated && !replacement)) return;
    await this.initializeAuth(generation, false, replacement);
  }

  // Actions

  // Computed values
  get userDisplayName(): string {
    const first = (this.currentUser?.first_name || '').trim().split(' ')[0];
    return first || 'User';
  }

  get photoUrl(): string | undefined {
    return this.currentUser?.photoUrl ?? undefined;
  }

  get userId(): string | null {
    return this.currentUser?.id || null;
  }

  get userFullDisplayName(): string {
    const first = (this.currentUser?.first_name || '').trim();
    const last = (this.currentUser?.last_name || '').trim();
    const full = [first, last].filter(Boolean).join(' ');
    return full || this.userPhoneNumber || 'User';
  }

  get userEmail(): string {
    // Use root-level email (backend response) or fallback to identity.email
    return this.currentUser?.email || this.currentUser?.identity?.email || '';
  }

  get userPhoneNumber(): string {
    // Use root-level phone_number (backend response), fallback to identity.phone,
    // or partner session msisdn for partner-authenticated users
    return (
      this.currentUser?.phone_number ||
      this.currentUser?.identity?.phone ||
      this.partnerAuthSession?.userData?.msisdn ||
      ''
    );
  }

  get userRegion(): string | null {
    // Try currentUser first (once backend deploys region field)
    if (this.currentUser?.region) {
      return this.currentUser.region;
    }
    // Fallback to registrationData (persisted in sessionStorage)
    if (this.registrationData?.region) {
      return this.registrationData.region;
    }
    return null;
  }

  get hasCoreOnboardingProfileData(): boolean {
    const firstName = (
      this.currentUser?.first_name ||
      this.registrationData.firstName ||
      this.registrationData.fullName?.split(' ')[0] ||
      ''
    ).trim();
    const region = (this.currentUser?.region || this.registrationData.region || '').trim();
    const dateOfBirth = this.normalizeDateForApi(
      this.resolveCurrentUserDateOfBirth() || this.registrationData.dateOfBirth
    );
    return Boolean(firstName && region && dateOfBirth);
  }

  get hasPersistedCoreOnboardingProfileData(): boolean {
    const firstName = (this.currentUser?.first_name || '').trim();
    const region = (this.currentUser?.region || '').trim();
    const dateOfBirth = this.normalizeDateForApi(this.resolveCurrentUserDateOfBirth());
    return Boolean(firstName && region && dateOfBirth);
  }

  isFullyOnboardedWithProfile(
    status: RegistrationStatus | null = this.registrationStatus
  ): boolean {
    return status === 'FULLY_ONBOARDED';
  }

  get isKycCompleted(): boolean {
    return (
      this.currentUser?.kyc_level !== 'NONE'
    );
  }

  get isEligibleForCredit(): boolean {
    if (!this.currentUser) return false;

    return (
      this.currentUser.kyc_level === 'FULL' 
    );
  }

  get isSignedIn(): boolean {
    return this.isAuthenticated && this.currentUser !== null;
  }

  get hasErrors(): boolean {
    return this.errors.length > 0 || this.error !== null;
  }

  get primaryError(): string | null {
    if (this.error) return this.error;
    if (this.errors.length > 0) return this.errors[0]?.message ?? null;
    return null;
  }

  get isPhoneAuthExpired(): boolean {
    if (!this.phoneAuthSession) return false;
    return new Date() > this.phoneAuthSession.expiresAt;
  }

  get phoneAuthTimeRemaining(): number {
    if (!this.phoneAuthSession) return 0;
    const remaining =
      this.phoneAuthSession.expiresAt.getTime() - new Date().getTime();
    return Math.max(0, Math.floor(remaining / 1000));
  }

  get canResendOTP(): boolean {
    return (
      this.phoneAuthSession !== null &&
      !this.isSendingOtp &&
      !this.isPhoneAuthExpired
    );
  }

  // Helper methods

  get isSessionNearExpiry(): boolean {
    if (!this.sessionExpiresAt) return false;
    const timeRemaining =
      this.sessionExpiresAt.getTime() - new Date().getTime();
    return timeRemaining < 15 * 60 * 1000; // 15 minutes
  }

  // New computed values for new auth methods
  get hasPartnerSession(): boolean {
    return this.partnerAuthSession !== null;
  }

  get isPartnerSessionExpired(): boolean {
    if (!this.partnerAuthSession) return false;
    return new Date() > this.partnerAuthSession.expiresAt;
  }

  get canAutoLoginPartner(): boolean {
    return (
      this.hasPartnerSession &&
      this.partnerAuthSession!.autoLoginAvailable &&
      !this.isPartnerSessionExpired
    );
  }

  get isNewPartnerUser(): boolean {
    return (
      this.hasPartnerSession &&
      this.partnerAuthSession!.userStatus === 'new_user'
    );
  }

  get canResumeRegistrationOnboarding(): boolean {
    if (!this.isAuthenticated || this.currentUser?.account_type !== 'CONSUMER') return false;
    const enteredPhone = this.otpAuthService.validatePhoneNumber(this.registrationData.phone || '');
    const accountPhone = this.otpAuthService.validatePhoneNumber(
      this.currentUser.phone_number || this.currentUser.identity?.phone || this.currentUser.username || ''
    );
    return Boolean(enteredPhone.isValid && accountPhone.isValid &&
      enteredPhone.formattedNumber === accountPhone.formattedNumber);
  }

  get canRetrySignupCode(): boolean {
    return this.otpSession?.purpose === 'SIGNUP' && !this.isOTPSessionExpired;
  }

  get hasOTPSession(): boolean {
    return this.otpSession !== null;
  }

  get hasCampaignBootstrapSession(): boolean {
    return (
      this.campaignBootstrapSession !== null && !this.isCampaignBootstrapSessionExpired
    );
  }

  get isCampaignBootstrapSessionExpired(): boolean {
    if (!this.campaignBootstrapSession) return false;
    return (
      new Date(this.campaignBootstrapSession.expiresAt).getTime() <= Date.now()
    );
  }

  get campaignBootstrapPhoneNumber(): string {
    return this.campaignBootstrapSession?.phoneNumber || '';
  }

  get isOTPSessionExpired(): boolean {
    if (!this.otpSession) return false;
    return new Date() > this.otpSession.expiresAt;
  }

  get otpTimeRemaining(): number {
    if (!this.otpSession) return 0;
    const remaining =
      this.otpSession.expiresAt.getTime() - new Date().getTime();
    return Math.max(0, Math.floor(remaining / 1000));
  }

  get canResendOTPNew(): boolean {
    return (
      this.hasOTPSession && !this.isOTPSessionExpired && !this.isRequestingOTP
    );
  }

  get hasAnonymousSession(): boolean {
    return this.anonymousSessionActive;
  }

  get authenticationMethod(): string {
    return this.lastLoginMethod || 'none';
  }

  get isFullyOnboarded(): boolean {
    return this.isFullyOnboardedWithProfile();
  }

  // Computed for checking registration completeness
  get isRegistrationComplete(): boolean {
    // Partner signup uses a separate validation contract from the backend.
    if (this.hasPartnerSession) {
      return true;
    }

    const required: Array<keyof RegistrationData> = [
      'phone',
      'region',
      'fullName',
      'dateOfBirth',
      'gender',
      'employmentStatus',
    ];

    if (!this.hasCampaignBootstrapSession) {
      required.push('otpCode');
    }

    return required.every((field) => {
      const value = this.registrationData[field];
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }
      return Boolean(value);
    });
  }

  private reportRegistrationStatusDiagnostic(
    reason: RegistrationStatusDiagnosticReason,
    source: string,
    options?: { rawStatus?: unknown; detail?: string }
  ): void {
    const rawStatus =
      options?.rawStatus === undefined || options.rawStatus === null
        ? undefined
        : String(options.rawStatus);
    const detail = options?.detail;
    const previous = this.registrationStatusDiagnostic;
    const unchanged =
      previous?.reason === reason &&
      previous?.source === source &&
      previous?.rawStatus === rawStatus &&
      previous?.detail === detail;

    if (unchanged) {
      return;
    }

    this.registrationStatusDiagnostic = {
      reason,
      source,
      rawStatus,
      detail,
      timestamp: Date.now(),
    };

    const payload: Record<string, unknown> = { reason, source };
    if (rawStatus !== undefined) {
      payload.rawStatus = rawStatus;
    }
    if (detail) {
      payload.detail = detail;
    }

    if (!REGISTRATION_STATUS_DIAGNOSTIC_SENTRY_ENABLED) {
      return;
    }

    const diagnosticKey = JSON.stringify({
      reason,
      source,
      rawStatus: rawStatus ?? '',
      detail: detail ?? '',
    });
    const now = Date.now();
    const shouldReportToSentry =
      diagnosticKey !== this.lastRegistrationDiagnosticSentryKey ||
      now - this.lastRegistrationDiagnosticSentryAt >=
        REGISTRATION_STATUS_DIAGNOSTIC_REPORT_WINDOW_MS;
    if (!shouldReportToSentry) {
      return;
    }
    this.lastRegistrationDiagnosticSentryKey = diagnosticKey;
    this.lastRegistrationDiagnosticSentryAt = now;
    withScope((scope) => {
      scope.setLevel('warning');
      scope.setTag('surface', 'auth-store');
      scope.setContext('registration_status_diagnostic', payload);
      captureMessage('[AuthStore] registration status diagnostic');
    });
  }

  private clearRegistrationStatusDiagnostic(): void {
    if (this.registrationStatusDiagnostic) {
      this.registrationStatusDiagnostic = null;
    }
  }

  private normalizeRegistrationStatus(
    status: unknown,
    source = 'AuthStore'
  ): RegistrationStatus | null {
    if (status === null || status === undefined) {
      this.reportRegistrationStatusDiagnostic('missing', source);
      return null;
    }

    if (typeof status !== 'string') {
      this.reportRegistrationStatusDiagnostic('invalid_type', source, {
        rawStatus: typeof status,
      });
      return null;
    }

    const normalized = status.trim().toUpperCase();
    if (!normalized) {
      this.reportRegistrationStatusDiagnostic('missing', source);
      return null;
    }

    if (!ALLOWED_REGISTRATION_STATUSES.has(normalized as RegistrationStatus)) {
      this.reportRegistrationStatusDiagnostic('unsupported', source, {
        rawStatus: status,
      });
      return null;
    }

    this.clearRegistrationStatusDiagnostic();
    return normalized as RegistrationStatus;
  }

  private normalizeAuthResponseRegistrationStatus(
    authResponse: {
      registration_status?: unknown;
      user?: { registration_status?: unknown } | null;
    },
    source: string
  ): RegistrationStatus | null {
    return this.normalizeRegistrationStatus(
      authResponse.registration_status ?? authResponse.user?.registration_status,
      source
    );
  }

  private formatDateForApi(dateValue?: string): string | undefined {
    if (!dateValue) return undefined;

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return undefined;
    }

    return format(parsedDate, 'yyyy-MM-dd');
  }

  private normalizeDateForApi(dateValue?: string): string | undefined {
    if (!dateValue) return undefined;

    // Preserve already-normalized date strings to avoid timezone shifts.
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }

    return this.formatDateForApi(dateValue);
  }

  private resolveCurrentUserDateOfBirth(): string | undefined {
    const dateOfBirth =
      this.currentUser?.date_of_birth || this.currentUser?.profile?.date_of_birth;

    if (typeof dateOfBirth === 'string') {
      return this.normalizeDateForApi(dateOfBirth);
    }

    if (dateOfBirth instanceof Date) {
      return format(dateOfBirth, 'yyyy-MM-dd');
    }

    return undefined;
  }

  private sanitizeRegistrationData(data: unknown): RegistrationData {
    if (!data || typeof data !== 'object') {
      return {};
    }

    const raw = data as RegistrationData;
    const sanitized: RegistrationData = { ...raw };

    if (sanitized.gender !== undefined) {
      if (
        typeof sanitized.gender !== 'string' ||
        !ALLOWED_REGISTRATION_GENDERS.has(sanitized.gender)
      ) {
        delete sanitized.gender;
      }
    }

    if (sanitized.employmentStatus !== undefined) {
      if (typeof sanitized.employmentStatus !== 'string') {
        delete sanitized.employmentStatus;
      } else {
        const normalizedEmploymentStatus = sanitized.employmentStatus
          .trim()
          .toUpperCase();
        if (ALLOWED_EMPLOYMENT_STATUSES.has(normalizedEmploymentStatus)) {
          sanitized.employmentStatus = normalizedEmploymentStatus;
        } else {
          delete sanitized.employmentStatus;
        }
      }
    }

    if (
      sanitized.whatsappSameNumber !== undefined &&
      typeof sanitized.whatsappSameNumber !== 'boolean'
    ) {
      delete sanitized.whatsappSameNumber;
    }

    if (sanitized.whatsappNumber !== undefined) {
      if (typeof sanitized.whatsappNumber === 'string') {
        sanitized.whatsappNumber = sanitized.whatsappNumber.trim();
      } else {
        delete sanitized.whatsappNumber;
      }
    }

    return sanitized;
  }

  private getSeparateWhatsAppNumber(): string | undefined {
    const whatsappNumber =
      typeof this.registrationData.whatsappNumber === 'string'
        ? this.registrationData.whatsappNumber.trim()
        : '';
    if (
      this.registrationData.whatsappSameNumber === false &&
      whatsappNumber
    ) {
      return whatsappNumber;
    }

    return undefined;
  }

  getPayload() {
    const email = this.registrationData.email?.trim();
    const separateWhatsAppNumber = this.getSeparateWhatsAppNumber();

    return {
      first_name:
        this.registrationData.firstName ||
        this.registrationData.fullName?.split(' ')[0] ||
        '',
      last_name:
        this.registrationData.lastName ||
        this.registrationData.fullName?.split(' ').slice(1).join(' ') ||
        '',
      email: email || undefined,
      marketing_consent: resolveMarketingConsent(
        this.registrationData.acceptsMarketing
      ),
      is_whatsapp_number: !separateWhatsAppNumber,
      whatsapp_number: separateWhatsAppNumber,
      // Additional profile data
      date_of_birth: this.normalizeDateForApi(this.registrationData.dateOfBirth),
      gender: this.registrationData.gender,
      occupation: this.registrationData.occupation,
      employment_status: this.registrationData.employmentStatus,
      residential_address: this.registrationData.residentialAddress,
      city: this.registrationData.city,
      region: this.registrationData.region,
      postal_code: this.registrationData.postalCode,
      about_us: this.registrationData.aboutUs,
    };
  }

  isFieldMissing(field: string): boolean {
    if (!this.hasPartnerSession) {
      return true;
    }

    const missingFields = this.partnerAuthSession?.missingFields;
    if (!Array.isArray(missingFields) || missingFields.length === 0) {
      return false;
    }

    return missingFields.includes(field);
  }

  // Initiate phone authentication with OTP
  async initiatePhoneAuth(phoneNumber: string): Promise<boolean> {
    try {
      this.isSendingOtp = true;
      this.clearErrors();

      if (!this.isValidPhoneNumber(phoneNumber)) {
        throw new Error('Invalid phone number format');
      }

      // Send OTP
      await this.sendOTP(phoneNumber, 'LOGIN');

      // Create phone auth session
      runInAction(() => {
        this.phoneAuthSession = {
          phoneNumber,
          otpSent: true,
          otpVerified: false,
          expiresAt: new Date(Date.now() + 10 * 60 * 1000), // 10 minutes
          attempts: 0,
          maxAttempts: 3,
        };
        this.isPhoneAuthInProgress = true;
      });

      return true;
    } catch (error: any) {
      this.addError({ message: error.message || 'Failed to send OTP' });
      return false;
    } finally {
      this.isSendingOtp = false;
    }
  }

  // Complete phone authentication with OTP
  async completePhoneAuth(otp: string): Promise<boolean> {
    if (!this.phoneAuthSession) {
      throw new Error('No active phone authentication session');
    }

    const authGeneration = this.beginAuthOperation();
    try {
      this.isVerifyingOtp = true;
      this.clearErrors();

      // Check if session is expired
      if (new Date() > this.phoneAuthSession.expiresAt) {
        throw new Error('OTP session expired. Please request a new OTP.');
      }

      // Check attempt limits
      if (this.phoneAuthSession.attempts >= this.phoneAuthSession.maxAttempts) {
        throw new Error(
          'Maximum OTP attempts exceeded. Please try again later.'
        );
      }

      // Increment attempts
      runInAction(() => {
        this.phoneAuthSession!.attempts += 1;
      });

      // Verify OTP
      const isValid = await this.verifyOTP(
        this.phoneAuthSession.phoneNumber,
        otp
      );

      this.assertAuthOperation(authGeneration);
      if (isValid) {
        this.settleAuthInitialization(this.beginAuthOperation());
        runInAction(() => {
          this.phoneAuthSession!.otpVerified = true;
          this.isAuthenticated = true;
          this.lastLoginMethod = 'phone';
          this.sessionExpiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000);
        });

        // Clear phone auth session
        this.clearPhoneAuthSession();
        return true;
      } else {
        if (
          this.phoneAuthSession.attempts >= this.phoneAuthSession.maxAttempts
        ) {
          this.clearPhoneAuthSession();
          throw new Error('Maximum OTP attempts exceeded');
        }
        throw new Error('Invalid OTP. Please try again.');
      }
    } catch (error: any) {
      if (authGeneration !== this.authGeneration) return false;
      this.addError({ message: error.message || 'OTP verification failed' });
      return false;
    } finally {
      this.isVerifyingOtp = false;
      await this.finishAuthOperation(authGeneration);
    }
  }

  async getCurrentUser(): Promise<void> {
    const authGeneration = this.authGeneration;
    this.setLoading(true);

    try {
      const user = await this.authenticateUserUseCase.getCurrentUser();
      if (authGeneration !== this.authGeneration) return;

      runInAction(() => {
        if (user) {
          this.currentUser = user;
          this.isAuthenticated = true;
          this.registrationStatus = this.normalizeRegistrationStatus(
            user.registration_status
          );
        } else {
          this.currentUser = null;
          this.isAuthenticated = false;
          this.registrationStatus = null;
          this.clearPersistedAuthTokens();
        }
      });
    } catch (_error) {
      if (authGeneration !== this.authGeneration) return;
      this.setError('We could not check your session. Please try again.');
    } finally {
      if (authGeneration === this.authGeneration) this.setLoading(false);
    }
  }

  async verifyOTP(
    phoneNumber: string,
    otp: string,
    purpose: OTPVerifyPurpose = 'VERIFICATION'
  ): Promise<boolean> {
    const authGeneration = this.authGeneration;
    this.setLoading(true);
    this.setError(null);

    try {
      await this.authenticateUserUseCase.validateOTP(phoneNumber, otp, purpose);

      return authGeneration === this.authGeneration;
    } catch (error) {
      if (authGeneration !== this.authGeneration) return false;
      this.setError(getApiErrorMessage(error, 'Failed to verify OTP'));
      return false;
    } finally {
      if (authGeneration === this.authGeneration) this.setLoading(false);
    }
  }

  async sendOTP(
    phoneNumber: string,
    purpose: 'SIGNUP' | 'LOGIN'
  ): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      await this.authenticateUserUseCase.requestOTP(phoneNumber, purpose);
    } catch (error) {
      this.setError(
        error instanceof Error ? error.message : 'Failed to send OTP'
      );
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  // async resetPassword(email: string): Promise<void> {
  //   this.setLoading(true);
  //   this.setError(null);
  //
  //   try {
  //     await this.authenticateUserUseCase.resetPassword(email);
  //   } catch (error) {
  //     this.setError(
  //       error instanceof Error ? error.message : 'Failed to reset password'
  //     );
  //     throw error;
  //   } finally {
  //     this.setLoading(false);
  //   }
  // }

  // Partner Authentication Methods
  async validatePartnerSession(
    sessionToken: string
  ): Promise<PartnerSessionValidateResponse> {
    const owner = this.authGeneration;
    this.isValidatingPartnerSession = true;
    this.clearErrors();

    try {
      const sessionData = await this.partnerAuthService.validatePartnerSession(
        sessionToken
      );
      this.assertAuthOperation(owner);

      runInAction(() => {
        this.partnerAuthSession = {
          sessionToken,
          userStatus: sessionData.user_status,
          autoLoginAvailable: sessionData.auto_login_available,
          accountLoginBlocked: sessionData.account_login_blocked ?? false,
          accountBlockReason: sessionData.account_block_reason ?? null,
          requiresOtp: sessionData.requires_otp ?? false,
          requiresOtpReason: sessionData.requires_otp_reason,
          missingFields: sessionData.missing_fields,
          userData: sessionData.user_data,
          partnerCode: sessionData.partner_code,
          campaignId: sessionData.campaign_id,
          expiresAt: new Date(sessionData.expires_at),
        };
        this.registrationData.dateOfBirth = sessionData.user_data.date_of_birth;
        this.registrationData.gender =
          sessionData.user_data.gender?.toLowerCase();
        this.registrationData.fullName = `${sessionData.user_data.first_name} ${sessionData.user_data.last_name}`;
      });

      return sessionData;
    } catch (error) {
      this.assertAuthOperation(owner);
      this.addError({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to validate partner session',
      });
      throw error;
    } finally {
      this.isValidatingPartnerSession = false;
    }
  }

  async registerPartnerUser(): Promise<void> {
    const data = this.registrationData;
    if (!this.partnerAuthSession) {
      throw new Error('No active partner session');
    }
    const separateWhatsAppNumber = this.getSeparateWhatsAppNumber();

    const additional_data: Record<string, unknown> = {
      whatsapp_same_number: !separateWhatsAppNumber,
    };
    if (separateWhatsAppNumber) {
      additional_data.whatsapp_number = separateWhatsAppNumber;
    }

    const email = data.email?.trim();
    if (email) additional_data.email = email;

    const region = data.region?.trim();
    if (region) additional_data.region = region;

    const employmentStatus = data.employmentStatus?.trim();
    if (employmentStatus) additional_data.employment_status = employmentStatus;

    const occupation = data.occupation?.trim();
    if (occupation) additional_data.occupation = occupation;

    const gender = data.gender?.trim();
    if (gender) additional_data.gender = gender;

    if (data.dateOfBirth) {
      additional_data.date_of_birth = this.normalizeDateForApi(data.dateOfBirth);
    }

    const payload = {
      session_token: this.partnerAuthSession.sessionToken,
      additional_data,
      terms_acceptance: {
        terms_accepted: '1.0',
        privacy_policy: '1.0',
        marketing_consent: resolveMarketingConsent(data.acceptsMarketing),
        accepted_at: new Date().toISOString(),
      },
      device_info: this.partnerAuthService.generateDeviceInfo(),
    };

    const entryGeneration = this.beginAuthOperation();
    let authGeneration = entryGeneration;
    let replacement = false;
    this.isRegisteringPartnerUser = true;
    this.clearErrors();

    try {
      this.prepareAuthReplacement(authGeneration);
      replacement = true;
      const authResponse = await this.partnerAuthService.registerPartnerUser(
        payload
      );

      // Ensure server-side auth cookies are set (middleware relies on access_token cookie).
      this.assertAuthOperation(authGeneration);
      this.persistAuthTokens({ accessToken: authResponse.access_token, refreshToken: authResponse.refresh_token });
      const serverSessionResult = await this.setServerSessionCookies({
        access_token: authResponse.access_token,
        refresh_token: authResponse.refresh_token,
        partner_code: this.partnerAuthSession?.partnerCode,
        expires_in: authResponse.expires_in,
      }, authGeneration);
      this.assertAuthOperation(authGeneration);
      if (!serverSessionResult.ok) {
        throw new Error(serverSessionResult.errorMessage);
      }

      authGeneration = this.beginAuthOperation();
      this.settleAuthInitialization(authGeneration);
      runInAction(() => {
        this.currentUser = authResponse.user as any;
        this.isAuthenticated = true;
        this.registrationStatus = this.normalizeAuthResponseRegistrationStatus(
          authResponse,
          'partner-register'
        );
        this.lastLoginMethod = 'partner';
        this.sessionExpiresAt = new Date(
          Date.now() + authResponse.expires_in * 1000
        );
        this.partnerAuthSession = null; // Clear session after successful registration
      });

      await this.ensureCurrentUserHydrated();
      this.assertAuthOperation(authGeneration);
      this.persistCurrentAuthSession();
    } catch (error) {
      this.assertAuthOperation(authGeneration);
      this.addError({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to register partner user',
      });
      throw error;
    } finally {
      this.isRegisteringPartnerUser = false;
      await this.finishAuthOperation(entryGeneration, replacement);
    }
  }

  async loginPartnerUser(): Promise<void> {
    if (!this.partnerAuthSession) {
      throw new Error('No active partner session');
    }

    const entryGeneration = this.beginAuthOperation();
    let authGeneration = entryGeneration;
    let replacement = false;
    this.isLoggingInPartnerUser = true;
    this.clearErrors();

    try {
      const request: PartnerLoginRequest = {
        session_token: this.partnerAuthSession.sessionToken,
        device_info: this.partnerAuthService.generateDeviceInfo(),
      };

      this.prepareAuthReplacement(authGeneration);
      replacement = true;
      const authResponse = await this.partnerAuthService.loginPartnerUser(
        request
      );

      // Ensure server-side auth cookies are set (middleware relies on access_token cookie).
      this.assertAuthOperation(authGeneration);
      this.persistAuthTokens({ accessToken: authResponse.access_token, refreshToken: authResponse.refresh_token });
      const serverSessionResult = await this.setServerSessionCookies({
        access_token: authResponse.access_token,
        refresh_token: authResponse.refresh_token,
        partner_code: this.partnerAuthSession?.partnerCode,
        expires_in: authResponse.expires_in,
      }, authGeneration);
      this.assertAuthOperation(authGeneration);
      if (!serverSessionResult.ok) {
        throw new Error(serverSessionResult.errorMessage);
      }

      authGeneration = this.beginAuthOperation();
      this.settleAuthInitialization(authGeneration);
      runInAction(() => {
        this.currentUser = authResponse.user as any;
        this.isAuthenticated = true;
        this.registrationStatus = this.normalizeAuthResponseRegistrationStatus(
          authResponse,
          'partner-login'
        );
        this.lastLoginMethod = 'partner';
        this.sessionExpiresAt = new Date(
          Date.now() + authResponse.expires_in * 1000
        );
        this.partnerAuthSession = null; // Clear session after successful login
      });

      await this.ensureCurrentUserHydrated();
      this.assertAuthOperation(authGeneration);
      this.persistCurrentAuthSession();
    } catch (error) {
      this.assertAuthOperation(authGeneration);
      this.addError({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to login partner user',
      });
      throw error;
    } finally {
      this.isLoggingInPartnerUser = false;
      await this.finishAuthOperation(entryGeneration, replacement);
    }
  }

  async redeemCampaignBootstrapToken(
    token: string
  ): Promise<CampaignBootstrapResponse> {
    const entryGeneration = this.beginAuthOperation();
    let authGeneration = entryGeneration;
    let replacement = false;
    this.isRedeemingCampaignBootstrap = true;
    this.clearErrors();

    try {
      this.prepareAuthReplacement(authGeneration);
      replacement = true;
      const response = await this.campaignBootstrapService.redeemCampaignBootstrap({
        token,
      });

      this.assertAuthOperation(authGeneration);
      if (response.status === 'existing_user') {
        this.assertAuthOperation(authGeneration);
        this.persistAuthTokens({ accessToken: response.access_token, refreshToken: response.refresh_token });
        const serverSessionResult = await this.setServerSessionCookies({
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          expires_in: response.expires_in,
        }, authGeneration);
        this.assertAuthOperation(authGeneration);
        if (!serverSessionResult.ok) {
          persistShopAuthHandoffPayload({
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
            expiresIn: response.expires_in,
            cleanUrl: '/',
          });
          return response;
        }

        authGeneration = this.beginAuthOperation();
        this.settleAuthInitialization(authGeneration);
        runInAction(() => {
          this.lastLoginMethod = 'campaign';
          this.sessionExpiresAt =
            typeof response.expires_in === 'number'
              ? new Date(Date.now() + response.expires_in * 1000)
              : null;
          this.campaignBootstrapSession = null;
        });

        this.clearCampaignBootstrapSession();
        await this.initializeAuth(authGeneration, false, true);
        this.assertAuthOperation(authGeneration);

        if (this.isAuthenticated && this.currentUser && this.sessionExpiresAt) {
          this.persistAuthSession({
            user: this.currentUser,
            expiresAt: this.sessionExpiresAt,
            method: 'campaign',
          });
        }

        return response;
      }

      const campaignSession = buildCampaignBootstrapSession(response);
      runInAction(() => {
        this.campaignBootstrapSession = campaignSession;
      });
      persistCampaignBootstrapSession(campaignSession);
      this.updateRegistrationData({
        phone: campaignSession.phoneNumber,
      });

      return response;
    } catch (error) {
      this.assertAuthOperation(authGeneration);
      this.clearCampaignBootstrapSession();
      this.addError({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to redeem campaign bootstrap link',
      });
      throw error;
    } finally {
      this.isRedeemingCampaignBootstrap = false;
      await this.finishAuthOperation(entryGeneration, replacement);
    }
  }

  // OTP Authentication Methods
  async lookupPhone(
    phoneNumber: string
  ): Promise<{ exists: boolean; message: string }> {
    this.isLookingUpPhone = true;
    this.clearErrors();

    try {
      const validation = this.otpAuthService.validatePhoneNumber(phoneNumber);
      if (!validation.isValid) {
        throw new Error(validation.error || 'Invalid phone number');
      }

      const request: PhoneLookupRequest = {
        msisdn: validation.formattedNumber,
      };

      const result = await this.otpAuthService.lookupPhone(request);
      return result;
    } catch (error) {
      this.addError({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to lookup phone number',
      });
      throw error;
    } finally {
      this.isLookingUpPhone = false;
    }
  }

  async lookupEmail(
    phoneNumber: string,
    email: string
  ): Promise<{ available: boolean; message: string }> {
    this.clearErrors();

    const validation = this.otpAuthService.validatePhoneNumber(phoneNumber);
    if (!validation.isValid) {
      throw new Error(validation.error || 'Invalid phone number');
    }

    const request: EmailLookupRequest = {
      email: (email || '').trim().toLowerCase(),
      msisdn: validation.formattedNumber,
    };

    try {
      return await this.otpAuthService.lookupEmail(request);
    } catch (error) {
      this.addError({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to lookup email address',
      });
      throw error;
    }
  }

  async requestOTPNew(
    phoneNumber: string,
    purpose: 'SIGNUP' | 'LOGIN'
  ): Promise<void> {
    this.isRequestingOTP = true;
    this.clearErrors();

    try {
      const validation = this.otpAuthService.validatePhoneNumber(phoneNumber);
      if (!validation.isValid) {
        throw new Error(validation.error || 'Invalid phone number');
      }

      // Check rate limiting
      const canRequest = this.otpAuthService.canRequestOTP(
        validation.formattedNumber
      );
      if (!canRequest.canRequest) {
        throw new Error(
          `Please wait ${canRequest.waitTime} seconds before requesting another OTP`
        );
      }

      const params: OTPRequestParams = {
        msisdn: validation.formattedNumber,
        purpose,
      };

      const response = await this.otpAuthService.requestOTP(params);
      const otpSession: OTPSession = {
        msisdn: validation.formattedNumber,
        requestId: response.request_id,
        purpose,
        expiresAt: new Date(Date.now() + response.expires_in * 1000),
        attempts: 0,
        maxAttempts: 3,
      };

      runInAction(() => {
        this.otpSession = otpSession;
      });
      this.persistOTPSession(otpSession);

      // Record request for rate limiting
      this.otpAuthService.recordOTPRequest(validation.formattedNumber);
    } catch (error) {
      this.addError({
        message:
          error instanceof Error ? error.message : 'Failed to request OTP',
      });
      throw error;
    } finally {
      this.isRequestingOTP = false;
    }
  }

  async registerWithOTP(
    otpCode: string,
    userData: {
      first_name: string;
      last_name: string;
      email?: string;
      marketing_consent?: boolean;
      region?: string;
      whatsapp_same_number?: boolean;
      is_whatsapp_number?: boolean;
      whatsapp_number?: string;
      date_of_birth?: string;
      gender?: string;
      marital_status?: string;
      occupation?: string;
      employment_status?: string;
    }
  ): Promise<void> {
    if (!this.otpSession || this.otpSession.purpose !== 'SIGNUP') {
      throw new Error('No active OTP signup session');
    }

    const entryGeneration = this.beginAuthOperation();
    let authGeneration = entryGeneration;
    let replacement = false;
    this.isRegisteringWithOTP = true;
    this.clearErrors();

    try {
      if (this.isOTPSessionExpired) {
        throw new Error('OTP session expired. Please request a new OTP.');
      }

      if (!this.otpAuthService.isValidOTP(otpCode)) {
        throw new Error('Invalid OTP format');
      }

      // Stop early when the local attempt cap has already been reached.
      if (this.otpSession.attempts >= this.otpSession.maxAttempts) {
        this.clearOTPSession();
        throw new Error('Maximum OTP attempts exceeded');
      }

      const request: OTPRegisterRequest = {
        msisdn: this.otpSession.msisdn,
        otp_code: otpCode,
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email?.trim() || undefined,
        marketing_consent: userData.marketing_consent,
        region: userData.region,
        is_whatsapp_number:
          userData.is_whatsapp_number ?? userData.whatsapp_same_number,
        whatsapp_number: userData.whatsapp_number,
        date_of_birth: this.normalizeDateForApi(userData.date_of_birth),
        gender: userData.gender,
        marital_status: userData.marital_status,
        occupation: userData.occupation,
        employment_status: userData.employment_status,
        device_info: this.otpAuthService.generateDeviceInfo(),
      };

      this.prepareAuthReplacement(authGeneration);
      replacement = true;
      const authResponse = await this.otpAuthService.registerWithOTP(request);

      // Ensure server-side auth cookies are set (middleware relies on access_token cookie).
      this.assertAuthOperation(authGeneration);
      this.persistAuthTokens({ accessToken: authResponse.access_token, refreshToken: authResponse.refresh_token });
      const serverSessionResult = await this.setServerSessionCookies({
        access_token: authResponse.access_token,
        refresh_token: authResponse.refresh_token,
        expires_in: authResponse.expires_in,
      }, authGeneration);
      this.assertAuthOperation(authGeneration);
      if (!serverSessionResult.ok) {
        throw new Error(serverSessionResult.errorMessage);
      }

      authGeneration = this.beginAuthOperation();
      this.settleAuthInitialization(authGeneration);
      runInAction(() => {
        this.currentUser = authResponse.user as any;
        this.isAuthenticated = true;
        this.registrationStatus = this.normalizeAuthResponseRegistrationStatus(
          authResponse,
          'otp-register'
        );
        this.lastLoginMethod = 'otp';
        this.sessionExpiresAt = new Date(
          Date.now() + authResponse.expires_in * 1000
        );
      });
      this.clearOTPSession();

      await this.ensureCurrentUserHydrated();
      this.assertAuthOperation(authGeneration);
      this.persistCurrentAuthSession();
    } catch (error: any) {
      this.assertAuthOperation(authGeneration);
      let finalError: Error =
        error instanceof Error ? error : new Error('Failed to register with OTP');

      if (this.shouldRecordOtpAttempt(error) && this.otpSession) {
        runInAction(() => {
          this.otpSession!.attempts += 1;
        });
        this.persistOTPSession(this.otpSession);

        if (this.otpSession.attempts >= this.otpSession.maxAttempts) {
          this.clearOTPSession();
          finalError = new Error('Maximum OTP attempts exceeded');
        }
      }

      this.addError({
        message: finalError.message,
      });
      throw finalError;
    } finally {
      this.isRegisteringWithOTP = false;
      await this.finishAuthOperation(entryGeneration, replacement);
    }
  }

  async registerWithCampaignBootstrap(userData: {
    first_name: string;
    last_name: string;
    email?: string;
    marketing_consent?: boolean;
    region?: string;
    whatsapp_same_number?: boolean;
    is_whatsapp_number?: boolean;
    whatsapp_number?: string;
    date_of_birth?: string;
    gender?: string;
    marital_status?: string;
    occupation?: string;
    employment_status?: string;
  }): Promise<void> {
    if (!this.hasCampaignBootstrapSession || !this.campaignBootstrapSession) {
      throw new Error('No active campaign bootstrap session');
    }

    const entryGeneration = this.beginAuthOperation();
    let authGeneration = entryGeneration;
    let replacement = false;
    this.isRegisteringWithCampaignBootstrap = true;
    this.clearErrors();

    try {
      const request = {
          bootstrap_session_id:
            this.campaignBootstrapSession.bootstrapSessionId,
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userData.email?.trim() || undefined,
          marketing_consent: userData.marketing_consent,
          region: userData.region,
          is_whatsapp_number:
            userData.is_whatsapp_number ?? userData.whatsapp_same_number,
          whatsapp_number: userData.whatsapp_number,
          date_of_birth: this.normalizeDateForApi(userData.date_of_birth),
          gender: userData.gender,
          marital_status: userData.marital_status,
          occupation: userData.occupation,
          employment_status: userData.employment_status,
          device_info: this.otpAuthService.generateDeviceInfo(),
        };
      this.prepareAuthReplacement(authGeneration);
      replacement = true;
      const authResponse = await this.otpAuthService.registerWithCampaignBootstrap(request);

      this.assertAuthOperation(authGeneration);
      this.persistAuthTokens({ accessToken: authResponse.access_token, refreshToken: authResponse.refresh_token });
      const serverSessionResult = await this.setServerSessionCookies({
        access_token: authResponse.access_token,
        refresh_token: authResponse.refresh_token,
        expires_in: authResponse.expires_in,
      }, authGeneration);
      this.assertAuthOperation(authGeneration);
      if (!serverSessionResult.ok) {
        throw new Error(serverSessionResult.errorMessage);
      }

      authGeneration = this.beginAuthOperation();
      this.settleAuthInitialization(authGeneration);
      runInAction(() => {
        this.currentUser = authResponse.user as any;
        this.isAuthenticated = true;
        this.registrationStatus = this.normalizeAuthResponseRegistrationStatus(
          authResponse,
          'campaign-register'
        );
        this.lastLoginMethod = 'campaign';
        this.sessionExpiresAt = new Date(
          Date.now() + authResponse.expires_in * 1000
        );
        this.campaignBootstrapSession = null;
      });

      this.clearCampaignBootstrapSession();
      await this.ensureCurrentUserHydrated();
      this.assertAuthOperation(authGeneration);
      this.persistAuthSession({
        user: this.currentUser ?? (authResponse.user as any),
        expiresAt: this.sessionExpiresAt!,
        method: 'campaign',
      });
    } catch (error) {
      this.assertAuthOperation(authGeneration);
      this.addError({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to register with campaign bootstrap',
      });
      throw error;
    } finally {
      this.isRegisteringWithCampaignBootstrap = false;
      await this.finishAuthOperation(entryGeneration, replacement);
    }
  }

  async loginWithOTP(otpCode: string): Promise<void> {
    if (!this.otpSession || this.otpSession.purpose !== 'LOGIN') {
      throw new Error('No active OTP login session');
    }

    const entryGeneration = this.beginAuthOperation();
    let authGeneration = entryGeneration;
    let replacement = false;
    this.isLoggingInWithOTP = true;
    this.clearErrors();

    try {
      if (this.isOTPSessionExpired) {
        throw new Error('OTP session expired. Please request a new OTP.');
      }

      if (!this.otpAuthService.isValidOTP(otpCode)) {
        throw new Error('Invalid OTP format');
      }

      // Stop early when the local attempt cap has already been reached.
      if (this.otpSession.attempts >= this.otpSession.maxAttempts) {
        this.clearOTPSession();
        throw new Error('Maximum OTP attempts exceeded');
      }

      const request: OTPLoginRequest = {
        msisdn: this.otpSession.msisdn,
        otp_code: otpCode,
        device_info: this.otpAuthService.generateDeviceInfo(),
      };

      this.prepareAuthReplacement(authGeneration);
      replacement = true;
      const authResponse = await this.otpAuthService.loginWithOTP(request);

      // Ensure server-side auth cookies are set (middleware relies on access_token cookie).
      this.assertAuthOperation(authGeneration);
      this.persistAuthTokens({ accessToken: authResponse.access_token, refreshToken: authResponse.refresh_token });
      const serverSessionResult = await this.setServerSessionCookies({
        access_token: authResponse.access_token,
        refresh_token: authResponse.refresh_token,
        expires_in: authResponse.expires_in,
      }, authGeneration);
      this.assertAuthOperation(authGeneration);
      if (!serverSessionResult.ok) {
        throw new Error(serverSessionResult.errorMessage);
      }

      authGeneration = this.beginAuthOperation();
      this.settleAuthInitialization(authGeneration);
      runInAction(() => {
        this.currentUser = authResponse.user as any;
        this.isAuthenticated = true;
        this.registrationStatus = this.normalizeAuthResponseRegistrationStatus(
          authResponse,
          'otp-login'
        );
        this.lastLoginMethod = 'otp';
        this.sessionExpiresAt = new Date(
          Date.now() + authResponse.expires_in * 1000
        );
      });
      this.clearOTPSession();

      await this.ensureCurrentUserHydrated();
      this.assertAuthOperation(authGeneration);
      this.persistCurrentAuthSession();
    } catch (error: any) {
      this.assertAuthOperation(authGeneration);
      let finalError: Error =
        error instanceof Error ? error : new Error('Failed to login with OTP');

      if (this.shouldRecordOtpAttempt(error) && this.otpSession) {
        runInAction(() => {
          this.otpSession!.attempts += 1;
        });
        this.persistOTPSession(this.otpSession);

        if (this.otpSession.attempts >= this.otpSession.maxAttempts) {
          this.clearOTPSession();
          finalError = new Error('Maximum OTP attempts exceeded');
        }
      }

      this.addError({
        message: finalError.message,
      });
      throw finalError;
    } finally {
      this.isLoggingInWithOTP = false;
      await this.finishAuthOperation(entryGeneration, replacement);
    }
  }

  // Anonymous Session Methods
  async createAnonymousSession(): Promise<void> {
    try {
      await this.anonymousSessionService.createAnonymousSession();
      runInAction(() => {
        this.anonymousSessionActive = true;
      });
    } catch (_error) {
      // Best-effort: anonymous session is an optimization, not a hard requirement.
    }
  }

  clearAnonymousSession(): void {
    this.anonymousSessionService.clearSession();
    runInAction(() => {
      this.anonymousSessionActive = false;
    });
  }

  async ensureAnonymousSession(): Promise<void> {
    if (!this.isAuthenticated && !this.hasAnonymousSession) {
      await this.createAnonymousSession();
    }
  }

  // Phone authentication helpers
  clearPhoneAuthSession(): void {
    runInAction(() => {
      this.phoneAuthSession = null;
      this.isPhoneAuthInProgress = false;
    });
  }

  async resendOTP(purpose: 'SIGNUP' | 'LOGIN' = 'SIGNUP'): Promise<boolean> {
    if (!this.phoneAuthSession) {
      return false;
    }

    try {
      await this.sendOTP(this.phoneAuthSession.phoneNumber, purpose);

      runInAction(() => {
        if (this.phoneAuthSession) {
          this.phoneAuthSession.expiresAt = new Date(
            Date.now() + 10 * 60 * 1000
          );
          this.phoneAuthSession.attempts = 0;
        }
      });

      return true;
    } catch (_error) {
      return false;
    }
  }

  // New session helpers
  clearPartnerAuthSession(): void {
    runInAction(() => {
      this.partnerAuthSession = null;
    });
  }

  clearOTPSession(): void {
    runInAction(() => {
      this.otpSession = null;
    });
    this.clearStoredOTPSession();
  }

  loadOTPSession(): void {
    const storedSession = this.readStoredOTPSession();
    if (!storedSession) {
      this.clearStoredOTPSession();
      return;
    }

    if (storedSession.expiresAt.getTime() <= Date.now()) {
      this.clearStoredOTPSession();
      return;
    }

    runInAction(() => {
      this.otpSession = storedSession;
    });
  }

  clearCampaignBootstrapSession(): void {
    runInAction(() => {
      this.campaignBootstrapSession = null;
    });
    clearStoredCampaignBootstrapSession();
  }

  loadCampaignBootstrapSession(): void {
    const storedSession = readCampaignBootstrapSession();

    if (!storedSession) {
      this.clearCampaignBootstrapSession();
      return;
    }

    runInAction(() => {
      this.campaignBootstrapSession = storedSession;
    });
  }

  parsePartnerTokenFromUrl(url: string): string | null {
    return this.partnerAuthService.parsePartnerToken(url);
  }

  formatPhoneNumberForDisplay(phoneNumber: string): string {
    return this.otpAuthService.formatPhoneForDisplay(phoneNumber);
  }

  validatePhoneNumberInput(phoneNumber: string): {
    isValid: boolean;
    formattedNumber: string;
    error?: string;
  } {
    return this.otpAuthService.validatePhoneNumber(phoneNumber);
  }

  formatOTPInput(input: string): string {
    return this.otpAuthService.formatOTPInput(input);
  }

  private persistOTPSession(session: OTPSession | null): void {
    if (typeof window === 'undefined') return;

    if (!session) {
      this.clearStoredOTPSession();
      return;
    }

    sessionStorage.setItem(
      OTP_SESSION_STORAGE_KEY,
      JSON.stringify({
        ...session,
        expiresAt: session.expiresAt.toISOString(),
      })
    );
  }

  private readStoredOTPSession(): OTPSession | null {
    if (typeof window === 'undefined') return null;

    try {
      const rawSession = sessionStorage.getItem(OTP_SESSION_STORAGE_KEY);
      if (!rawSession) return null;

      const parsed = JSON.parse(rawSession) as Partial<
        Omit<OTPSession, 'expiresAt'> & { expiresAt: string }
      >;

      if (
        typeof parsed.msisdn !== 'string' ||
        typeof parsed.requestId !== 'string' ||
        (parsed.purpose !== 'SIGNUP' && parsed.purpose !== 'LOGIN') ||
        typeof parsed.expiresAt !== 'string'
      ) {
        return null;
      }

      const expiresAt = new Date(parsed.expiresAt);
      if (Number.isNaN(expiresAt.getTime())) return null;

      return {
        msisdn: parsed.msisdn,
        requestId: parsed.requestId,
        purpose: parsed.purpose,
        expiresAt,
        attempts: typeof parsed.attempts === 'number' ? parsed.attempts : 0,
        maxAttempts:
          typeof parsed.maxAttempts === 'number' ? parsed.maxAttempts : 3,
      };
    } catch {
      return null;
    }
  }

  private clearStoredOTPSession(): void {
    if (typeof window === 'undefined') return;

    sessionStorage.removeItem(OTP_SESSION_STORAGE_KEY);
  }

  private shouldRecordOtpAttempt(error: any): boolean {
    const payload = error?.response?.data ?? error?.data;
    // HTTP 400 also covers profile/account failures; only wrong codes use this budget.
    return payload?.code === 'invalid_otp' || payload?.details?.code === 'invalid_otp';
  }

  isValidOTPInput(otp: string): boolean {
    return this.otpAuthService.isValidOTP(otp);
  }

  // Enhanced sign out to clear all sessions
  async signOutComplete(): Promise<void> {
    const authGeneration = this.beginAuthOperation();
    clearCheckoutPaymentObservations();
    this.setSigningOut(true);

    try {
      // Clear all authentication states
      runInAction(() => {
        this.currentUser = null;
        this.isAuthenticated = false;
        this.registrationStatus = null;
        this.partnerAuthSession = null;
        this.otpSession = null;
        this.campaignBootstrapSession = null;
        this.phoneAuthSession = null;
        this.lastLoginMethod = null;
        this.sessionExpiresAt = null;
      });

      // Clear persisted data
      this.clearPersistedAuthTokens();
      this.clearPersistedAuthSession();
      this.clearStoredOTPSession();
      this.clearCampaignBootstrapSession();
      this.clearAnonymousSession();
      this.settleAuthInitialization(authGeneration);

      // Call original sign out if needed
      if (this.authenticateUserUseCase) {
        await this.signOut();
      }
    } catch (error) {
      this.setError(
        error instanceof Error ? error.message : 'Failed to sign out'
      );
    } finally {
      this.setSigningOut(false);
    }
  }

  // Error management
  addError(error: AuthError): void {
    runInAction(() => {
      this.errors.push(error);
    });
  }

  clearErrors(): void {
    runInAction(() => {
      this.errors = [];
      this.error = null;
    });
  }

  removeError(index: number): void {
    runInAction(() => {
      this.errors.splice(index, 1);
    });
  }

  // Session management
  setRememberMe(remember: boolean): void {
    this.rememberMe = remember;
  }

  // Validation helpers
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  isValidPhoneNumber(phone: string): boolean {
    // Ghana phone number format
    const phoneRegex =
      /^(\+233|0)(2[0-9]|5[0-4]|24|54|27|57|26|56|23|28)[0-9]{7}$/;
    return phoneRegex.test(phone);
  }

  formatPhoneNumber(phone: string): string {
    // Convert to international format
    if (phone.startsWith('0')) {
      return '+233' + phone.substring(1);
    }
    if (!phone.startsWith('+233')) {
      return '+233' + phone;
    }
    return phone;
  }

  // Session expiry management
  checkSessionExpiry(): boolean {
    if (this.sessionExpiresAt && new Date() > this.sessionExpiresAt) {
      this.handleSessionExpiry();
      return true;
    }
    return false;
  }

  extendSession(): void {
    runInAction(() => {
      this.sessionExpiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000);
      this.isSessionExpiring = false;
    });
  }

  // Registration data management
  updateRegistrationData(data: Partial<RegistrationData>): void {
    runInAction(() => {
      this.registrationData = { ...this.registrationData, ...data };
    });

    // Persist to session storage for cross-component access
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(
        'registrationData',
        JSON.stringify(this.registrationData)
      );
    }
  }

  clearRegistrationData(): void {
    runInAction(() => {
      this.registrationData = {};
      this.signUpStepNumber = 0;
    });

    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('registrationData');
    }
  }

  loadRegistrationData(): void {
    if (typeof window !== 'undefined') {
      const storedData = sessionStorage.getItem('registrationData');
      if (storedData) {
        try {
          const parsedData = this.sanitizeRegistrationData(JSON.parse(storedData));
          runInAction(() => {
            this.registrationData = parsedData;
          });
        } catch (_error) {
          // Ignore invalid cached registration payloads.
        }
      }
    }
  }

  setSignUpStepNumber(stepNumber: number): void {
    runInAction(() => {
      this.signUpStepNumber = Math.max(0, stepNumber);
    });
  }

  private async parseApiError(response: Response): Promise<string> {
    try {
      const payload = (await response.json()) as {
        detail?: string;
        message?: string;
        error?: string;
      };
      return (
        payload.detail ||
        payload.message ||
        payload.error ||
        `Request failed (${response.status})`
      );
    } catch {
      return `Request failed (${response.status})`;
    }
  }

  private async hydrateCurrentUserFromServer(): Promise<void> {
    const authGeneration = this.authGeneration;
    try {
      const user = await this.authenticateUserUseCase.getCurrentUser();
      if (authGeneration !== this.authGeneration || !user) return;
      runInAction(() => {
        this.currentUser = user;
        if ('registration_status' in user) {
          this.registrationStatus = this.normalizeRegistrationStatus(
            user.registration_status,
            'hydrateCurrentUserFromServer'
          );
        } else {
          this.reportRegistrationStatusDiagnostic(
            'missing',
            'hydrateCurrentUserFromServer:preserved'
          );
        }
      });
      this.persistCurrentAuthSession();
    } catch {
      if (authGeneration !== this.authGeneration) return;
      this.reportRegistrationStatusDiagnostic(
        'fetch_failed',
        'hydrateCurrentUserFromServer',
        { detail: 'session_state_unknown' }
      );
    }
  }

  async ensureCurrentUserHydrated(): Promise<void> {
    if (this.currentUser && this.hasPersistedCoreOnboardingProfileData) return;
    await this.hydrateCurrentUserFromServer();
  }

  private persistCurrentAuthSession(): void {
    if (!this.currentUser || !this.sessionExpiresAt || !this.lastLoginMethod) {
      return;
    }

    this.persistAuthSession({
      user: this.currentUser,
      expiresAt: this.sessionExpiresAt,
      method: this.lastLoginMethod,
    });
  }

  async refreshCurrentUser(): Promise<void> {
    await this.hydrateCurrentUserFromServer();
  }

  async refreshRegistrationStatus(): Promise<RegistrationStatus | null> {
    const authGeneration = this.authGeneration;
    if (!this.isAuthenticated) {
      runInAction(() => {
        this.registrationStatus = null;
      });
      this.clearRegistrationStatusDiagnostic();
      return null;
    }

    const previousStatus = this.registrationStatus;
    try {
      const response = await fetch('/api/proxy/v1/auth/registration-status', {
        cache: 'no-store',
      });
      if (authGeneration !== this.authGeneration) return null;
      if (!response.ok) {
        this.reportRegistrationStatusDiagnostic(
          'fetch_failed',
          'refreshRegistrationStatus',
          { detail: `HTTP ${response.status}` }
        );
        return previousStatus;
      }

      const payload = (await response.json()) as { status?: string };
      if (authGeneration !== this.authGeneration) return null;
      const normalized = this.normalizeRegistrationStatus(
        payload.status,
        'refreshRegistrationStatus'
      );
      if (!normalized) {
        return previousStatus;
      }
      runInAction(() => {
        this.registrationStatus = normalized;
        if (this.currentUser) {
          this.currentUser.registration_status = normalized;
        }
      });
      this.persistCurrentAuthSession();
      return normalized;
    } catch {
      if (authGeneration !== this.authGeneration) return null;
      this.reportRegistrationStatusDiagnostic(
        'fetch_failed',
        'refreshRegistrationStatus',
        { detail: 'unknown_error' }
      );
      return previousStatus;
    }
  }

  async completeOnboardingProgress(params: {
    marketingConsent: boolean;
    referralSource: string;
    termsVersion?: string;
    privacyVersion?: string;
  }): Promise<void> {
    const authGeneration = this.authGeneration;
    const termsVersion = params.termsVersion ?? '1.0';
    const privacyVersion = params.privacyVersion ?? '1.0';
    const completePayload = {
      terms_version: termsVersion,
      privacy_version: privacyVersion,
      marketing_consent: params.marketingConsent,
      referral_source: params.referralSource,
    };
    const completeResponse = await fetch(
      '/api/proxy/v1/users/me/complete-onboarding',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(completePayload),
      }
    );

    this.assertAuthOperation(authGeneration);
    // Backward-compatible fallback for environments where atomic endpoint
    // is not deployed yet.
    if (completeResponse.status === 404) {
      const termsResponse = await fetch('/api/proxy/v1/users/me/accept-terms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          terms_version: termsVersion,
          privacy_version: privacyVersion,
          marketing_consent: params.marketingConsent,
        }),
      });
      this.assertAuthOperation(authGeneration);
      if (!termsResponse.ok) {
        throw new Error(await this.parseApiError(termsResponse));
      }

      const referralResponse = await fetch(
        '/api/proxy/v1/users/me/referral-source',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            referral_source: params.referralSource,
          }),
        }
      );
      this.assertAuthOperation(authGeneration);
      if (!referralResponse.ok) {
        throw new Error(
          `Terms were accepted, but we could not save your referral source: ${await this.parseApiError(
            referralResponse
          )}`
        );
      }
    } else if (!completeResponse.ok) {
      throw new Error(await this.parseApiError(completeResponse));
    } else {
      const payload = (await completeResponse.json().catch(() => null)) as
        | { registration_status?: string }
        | null;
      this.assertAuthOperation(authGeneration);
      const normalized = this.normalizeRegistrationStatus(
        payload?.registration_status
      );
      if (normalized) {
        runInAction(() => {
          this.registrationStatus = normalized;
          if (this.currentUser) {
            this.currentUser.registration_status = normalized;
          }
        });
      }
    }

    const refreshedStatus =
      this.registrationStatus === 'FULLY_ONBOARDED'
        ? this.registrationStatus
        : await this.refreshRegistrationStatus();
    this.assertAuthOperation(authGeneration);
    if (refreshedStatus !== 'FULLY_ONBOARDED') {
      throw new Error(
        'We could not confirm onboarding completion yet. Please try again.'
      );
    }

    try {
      await this.refreshCurrentUser();
    } catch {
      // Best-effort refresh. The onboarding completion itself already succeeded.
    }
  }

  async signOut(options?: { preserveCheckoutObservation: boolean }): Promise<void> {
    const preserve = options?.preserveCheckoutObservation === true;
    const wasSigningOut = this.isSigningOut;
    if (preserve && wasSigningOut) throw new Error('Session cleanup is already in progress');
    const authGeneration = this.beginAuthOperation();
    if (preserve) this.setSigningOut(true);
    try {
      if (preserve) {
        const currentAccountId = this.currentUser?.id;
        // Protected pages redirect on local logout; cookies must be cleared first.
        await this.clearSessionCookies(true);
        this.assertAuthOperation(authGeneration);
        if (this.currentUser?.id !== currentAccountId) throw new Error('Account changed during session cleanup');
        invalidateCheckoutObservation();
      } else clearCheckoutPaymentObservations();
      runInAction(() => {
        this.currentUser = null;
        this.isAuthenticated = false;
        this.registrationStatus = null;
        this.sessionExpiresAt = null;
        this.lastLoginMethod = null;
      });

      this.clearPersistedAuthTokens();
      this.clearPersistedAuthSession();
      this.clearCampaignBootstrapSession();
      this.clearRegistrationData();

      if (typeof window === 'undefined') return;
      localStorage.removeItem('motito_auth_token');
      localStorage.removeItem('motito_auth_expiry');
      if (!preserve) await this.clearSessionCookies(false);
    } finally {
      if (preserve) this.setSigningOut(wasSigningOut);
      this.settleAuthInitialization(authGeneration);
    }
  }

  private async clearSessionCookies(preserveCheckoutObservation: boolean): Promise<void> {
    if (typeof window === 'undefined') return;
    const generation = this.authGeneration;
    const controller = preserveCheckoutObservation ? new AbortController() : null;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const deadline = controller ? new Promise<never>((_resolve, reject) => {
      timeout = setTimeout(() => {
        controller.abort();
        reject(new Error('Session cleanup aborted. Please try again.'));
      }, 15_000);
    }) : null;
    try {
      const clearing = writeAuthCookies(generation, async () => {
        // A cleanup that expired in the queue must never clear a later session.
        if (controller?.signal.aborted) throw new Error('Session cleanup aborted');
        const response = await fetch('/api/auth/clear-session', {
          method: 'POST', ...(controller ? { signal: controller.signal } : {}),
        });
        if (preserveCheckoutObservation && !response.ok) throw new Error('Session could not be cleared');
      });
      await (deadline ? Promise.race([clearing, deadline]) : clearing);
    } catch (error) {
      this.assertAuthOperation(generation);
      if (preserveCheckoutObservation) throw error;
      // Local credentials are already cleared if server cleanup fails.
    } finally {
      if (timeout !== undefined) clearTimeout(timeout);
    }
  }

  private async checkServerSession(): Promise<boolean> {
    if (typeof window === 'undefined') return false;
    const response = await fetch('/api/auth/session', { cache: 'no-store' });
    if (response.status === 401 || response.status === 403) return false;
    if (!response.ok) throw new Error('Session check unavailable');
    const data = (await response.json()) as { authenticated?: boolean };
    if (typeof data?.authenticated !== 'boolean') throw new Error('Invalid session response');
    return data.authenticated;
  }

  private async refreshServerSession(generation: number): Promise<boolean> {
    if (typeof window === 'undefined') return false;
    return writeAuthCookies(generation, async () => {
      const response = await fetch('/api/auth/refresh', { method: 'POST' });
      if (response.status === 401 || response.status === 403) return false;
      if (!response.ok) throw new Error('Session refresh unavailable');
      const data = await response.json();
      const access = data?.access_token ?? data?.access;
      if (typeof access !== 'string' || !access.trim()) throw new Error('Invalid session refresh');
      return true;
    });
  }

  private async checkOrRefreshServerSession(generation: number): Promise<boolean> {
    const authenticated = await this.checkServerSession();
    if (generation !== this.authGeneration) return false;
    if (authenticated) return true;

    const refreshed = await this.refreshServerSession(generation);
    if (generation !== this.authGeneration || !refreshed) return false;
    const authenticatedAfterRefresh = await this.checkServerSession();
    return generation === this.authGeneration && authenticatedAfterRefresh;
  }

  private async setServerSessionCookies(payload: {
    access_token: string;
    refresh_token?: string;
    partner_code?: string;
    expires_in?: number;
  }, authGeneration: number): Promise<ServerSessionCookieResult> {
    this.assertAuthOperation(authGeneration);
    if (typeof window === 'undefined') {
      return {
        ok: false,
        errorMessage: 'We could not finish signing you in. Please try again.',
      };
    }
    try {
      const response = await writeAuthCookies(authGeneration, () => fetch('/api/auth/set-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }));
      this.assertAuthOperation(authGeneration);
      if (response.ok) {
        return { ok: true };
      }

      return {
        ok: false,
        status: response.status,
        errorMessage: await this.parseApiError(response),
      };
    } catch {
      return {
        ok: false,
        errorMessage: 'We could not finish signing you in. Please try again.',
      };
    }
  }

  private persistAuthTokens(tokens: {
    accessToken: string;
    refreshToken: string;
  }): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem('auth_tokens', JSON.stringify(tokens));
    }
  }

  private getPersistedAuthTokens(): {
    accessToken: string;
    refreshToken: string;
  } | null {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('auth_tokens');
      return stored ? JSON.parse(stored) : null;
    }
    return null;
  }

  private clearPersistedAuthTokens(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_tokens');
    }
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  private setSigningIn(signingIn: boolean): void {
    this.isSigningIn = signingIn;
  }

  private setSigningOut(signingOut: boolean): void {
    this.isSigningOut = signingOut;
  }

  private setError(error: string | null): void {
    this.error = error;
  }

  private persistAuthSession(session: {
    user: any;
    expiresAt: Date;
    method: 'email' | 'phone' | 'partner' | 'otp' | 'campaign';
  }): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(
        'auth_session',
        JSON.stringify({
          ...session,
          expiresAt: session.expiresAt.toISOString(),
        })
      );
    }
  }

  private getPersistedAuthSession(): {
    user: User;
    expiresAt: Date;
    method: 'email' | 'phone' | 'partner' | 'otp' | 'campaign';
  } | null {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('auth_session');
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...parsed,
          expiresAt: new Date(parsed.expiresAt),
        };
      }
    }
    return null;
  }

  private clearPersistedAuthSession(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_session');
    }
  }

  private handleSessionExpiry(): void {
    runInAction(() => {
      this.isSessionExpiring = true;
    });

    // Auto sign out after 5 minutes warning
    setTimeout(() => {
      if (this.isSessionExpiring) {
        void this.signOut();
      }
    }, 5 * 60 * 1000);
  }

  recoverServerSession(): Promise<AuthRecoveryResult> {
    return this.initializeAuth(this.authGeneration);
  }

  private initializeAuth(owner: number, allowLegacyToken = true, replacement = false): Promise<AuthRecoveryResult> {
    if (this.initialization?.owner === owner) return this.initialization.promise;
    const promise = this.reconcileAuthSession(owner, allowLegacyToken, replacement);
    this.initialization = { owner, promise };
    void promise.finally(() => {
      if (this.initialization?.promise === promise) this.initialization = null;
    });
    return promise;
  }

  private async reconcileAuthSession(owner: number, allowLegacyToken: boolean, replacement: boolean): Promise<AuthRecoveryResult> {
    if (owner !== this.authGeneration) return 'superseded';
    const hydrating = !this.isHydrated;
    if (hydrating) this.setLoading(true);
    let admitted = false;
    try {
      await waitForAuthCookies(owner);
      this.loadCampaignBootstrapSession();
      const storedSession = this.getPersistedAuthSession();
      if (!this.isHydrated && allowLegacyToken && storedSession) {
        runInAction(() => {
          this.currentUser = storedSession.user;
          this.isAuthenticated = new Date() < storedSession.expiresAt;
          this.registrationStatus = this.normalizeRegistrationStatus(storedSession.user?.registration_status);
          this.sessionExpiresAt = storedSession.expiresAt;
          this.lastLoginMethod = storedSession.method;
        });
      }

      admitted = await this.checkOrRefreshServerSession(owner);
      this.assertAuthOperation(owner);
      if (!admitted && allowLegacyToken && typeof window !== 'undefined') {
        const tokens = this.getPersistedAuthTokens();
        const access = localStorage.getItem('motito_auth_token') || tokens?.accessToken;
        if (access) {
          const result = await this.setServerSessionCookies({
            access_token: access, refresh_token: tokens?.refreshToken || undefined,
          }, owner);
          this.assertAuthOperation(owner);
          if (!result.ok && result.status !== 401 && result.status !== 403) {
            throw new Error('Session recovery unavailable');
          }
          admitted = result.ok && await this.checkServerSession();
          this.assertAuthOperation(owner);
        }
      }

      // Cookie admission and a real profile are both required. Transport failures
      // retain the cached identity for retry; only an explicit rejection clears it.
      const user = admitted ? await this.authenticateUserUseCase.getCurrentUser() : null;
      this.assertAuthOperation(owner);
      if (!user) {
        if (admitted) await this.clearSessionCookies(true);
        this.assertAuthOperation(owner);
        this.clearPersistedAuthTokens();
        this.clearPersistedAuthSession();
        if (typeof window !== 'undefined') {
          localStorage.removeItem('motito_auth_token');
          localStorage.removeItem('motito_auth_expiry');
        }
        runInAction(() => {
          this.currentUser = null;
          this.isAuthenticated = false;
          this.registrationStatus = null;
          this.sessionExpiresAt = null;
          this.lastLoginMethod = null;
        });
        return 'rejected';
      }
      runInAction(() => {
        this.currentUser = user;
        this.isAuthenticated = true;
        this.registrationStatus = this.normalizeRegistrationStatus(user.registration_status);
      });
      await this.refreshRegistrationStatus();
      this.assertAuthOperation(owner);
      this.persistCurrentAuthSession();
      this.setError(null);
      return 'authenticated';
    } catch {
      if (owner !== this.authGeneration) return 'superseded';
      if (!admitted || replacement) this.isAuthenticated = false;
      this.setError('We could not check your session. Please try again.');
      return 'unavailable';
    } finally {
      if (hydrating) this.settleAuthInitialization(owner);
    }
  }

}

export interface AuthError {
  field?: string;
  message: string;
  code?: string;
}

export interface LoginCredentials {
  email?: string;
  phone?: string;
  password: string;
}

export interface PhoneAuthSession {
  phoneNumber: string;
  sessionId?: string;
  otpSent: boolean;
  otpVerified: boolean;
  expiresAt: Date;
  attempts: number;
  maxAttempts: number;
}
export interface LoginAuthSession {
  user:      User;
  expiresAt: Date;
  method:    string;
}

export interface User {
  id:              string;
  username:        string;
  account_type:    string;
  first_name:      string;
  last_name:       string;
  // Contact info - returned at root level by backend
  email:           string | null;
  phone_number:    string | null;
  email_verified:  boolean;
  msisdn_verified: boolean;
  // Other fields
  kyc_level:       string;
  account_status:  string;
  permissions:     string[];
  roles:           Role[];
  // Region - returned by backend from profile.region
  region?:         string | null;
  date_of_birth?:  string | null;
  // Optional nested objects (may not always be present)
  identity?:       Identity;
  profile?:        Profile;
  primary_role?:   Role | null;
  partner_org_code?: string;
  acquisition_source?: string | null;
  referral_source?: string | null;
  registration_status?: string | null;
  photoUrl?: string | null;
}

export interface Identity {
  id:             string;
  email:          string;
  email_verified: boolean;
  phone:          string;
  phone_verified: boolean;
}

export interface Profile {
  first_name:    string;
  last_name:     string;
  date_of_birth: Date | string | null;
  kyc_level:     string;
}

export interface Role {
  code: string;
  name: string;
}



export interface PartnerAuthSession {
  sessionToken: string;
  userStatus: 'new_user' | 'existing_user';
  missingFields?: string[];
  autoLoginAvailable: boolean;
  accountLoginBlocked?: boolean;
  accountBlockReason?: string | null;
  requiresOtp: boolean;
  requiresOtpReason?: string;
  userData: any;
  partnerCode: string;
  campaignId: string;
  expiresAt: Date;
}

export interface OTPSession {
  msisdn: string;
  requestId: string;
  purpose: 'SIGNUP' | 'LOGIN';
  expiresAt: Date;
  attempts: number;
  maxAttempts: number;
}

export interface CampaignBootstrapSession {
  bootstrapSessionId: string;
  phoneNumber: string;
  expiresAt: string;
}

export interface RegistrationData {
  whatsappSameNumber?: boolean;
  whatsappNumber?: string;
  phone?: string;
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  dateOfBirth?: string;
  gender?: string;
  occupation?: string;
  residentialAddress?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  aboutUs?: string;
  acceptsMarketing?: boolean;
  otpCode?: string; // Verified OTP code for final registration
  employmentStatus?: string;
}
